/**
 * @file そだちの土台カタログ (伸びやすい時期のある土台と、わが家の打ち手)。
 *
 * @what: 領域を横断する「土台」6 件。伸びどきの目安・一言の説明・打ち手 (タブや本だなへの
 *   導線)・根拠を持つ。計画タブの「そだちの土台」カードが読む。
 * @why: 音感や色彩のような発達の話を、測らず「いま何に触れさせるか」の判断材料として
 *   置く (代表 2026-08-24 草案承認)。採否の経緯: 色彩感覚は伸びどきの根拠が弱いため不採用
 *   (京大 2025 で 3 歳児は大人と同様の色の感じ方)、英語の音は乳児期の適期が過ぎているため不採用。
 * @failure: 描画専用の定数。出典の source id が本だなに無い環境ではリンクを出さない (文言は出る)。
 */

import type { StudyViewId } from "../components/studyViews";

/** 打ち手が飛べるタブ (アプリ全体のタブ定義の部分集合。タブ改名時に型で追随する)。 */
/** 打ち手が開く面で、"map" は計画の面の「単元 × ステップ」の見せ方を指す。 */
export type SodachiTabView = Extract<StudyViewId, "motor" | "music"> | "map";

/** 打ち手の導線。tab = スタディのタブへ、source = 本だなの該当ページへ (根拠として出す)。 */
export type SodachiAction =
  | { kind: "tab"; view: SodachiTabView; label: string }
  | { kind: "source"; sourceId: string; page?: number; label: string };

export interface SodachiEntry {
  id: string;
  label: string;
  /** 伸びどきの目安 (断定しない表記)。 */
  timing: string;
  /** 一言の説明 (何が伸びるか・なぜ今か)。 */
  note: string;
  actions: readonly SodachiAction[];
  /** 根拠 (研究・指針)。本だなで裏づけられるものは actions に source で入れる。 */
  evidence: string;
}

export const SODACHI_CATALOG: readonly SodachiEntry[] = [
  {
    id: "pitch_ear",
    label: "音の高さを聴き分ける耳",
    timing: "6 歳半ごろまで",
    note: "和音を聴き分ける力は幼児期にしか身につかないとされます。訓練に 2〜3 年かかるので、始めるなら 3〜4 歳です",
    actions: [
      { kind: "tab", view: "music", label: "おんがくタブ（きき分けあそび）" },
      { kind: "source", sourceId: "onkan-program", page: 16, label: "『新・絶対音感プログラム』第1章" },
    ],
    evidence: "榊原（2004・教育心理学研究）の和音判別訓練の縦断研究にもとづきます",
  },
  {
    id: "rhythm",
    label: "リズム感",
    timing: "幼児期の全体",
    note: "伸びやすい時期がはっきり決まっているわけではありませんが、動きに合わせる経験で伸びます。週メニューのスキップやリズム運動が担います",
    actions: [{ kind: "tab", view: "motor", label: "うんどうタブ（スキップ・リズム群）" }],
    evidence: "特定の時期に限られる強い根拠はなく、発達に沿って伸びるものとされます",
  },
  {
    id: "phonological",
    label: "音韻意識（ことばを音に分ける）",
    timing: "3〜5 歳に大きく伸びる",
    note: "しりとりや同じ音さがしができることが、読み書きの前提になります",
    actions: [
      { kind: "tab", view: "map", label: "単元 × ステップ（しりとり・同頭音同尾音）" },
    ],
    evidence: "かな習得の研究で、音韻意識が読みの習得を予測すると繰り返し確かめられています",
  },
  {
    id: "varied_movement",
    label: "多様な動き（平衡・協調）",
    timing: "幼児期から小学校低学年",
    note: "神経系の発達が先に進む時期なので、特定の種目にしぼらず多様な動きに触れます",
    actions: [
      { kind: "tab", view: "motor", label: "うんどうタブ（週メニュー全体）" },
      { kind: "source", sourceId: "undou-asobi-0-5", page: 26, label: "『0〜5歳児の…運動あそび』神経型の発達" },
    ],
    evidence: "文部科学省「幼児期運動指針」（2012 年）の毎日合計 60 分・多様な動きにもとづきます",
  },
  {
    id: "fine_motor",
    label: "巧緻性（手指）",
    timing: "3 歳のはさみから段階的に",
    note: "発達の順序が定まっている領域で、見取り図の巧緻性の幹に組み込んであります",
    actions: [{ kind: "tab", view: "map", label: "単元 × ステップ（巧緻性の幹）" }],
    evidence: "発達の順序として確立しています",
  },
  {
    id: "executive_function",
    label: "実行機能（がまん・切り替え）",
    timing: "3〜5 歳に大きく伸びる",
    note: "学力と強く結びつくのは知能指数より実行機能とされます。「動」と「静」の切り替え遊びが担います",
    actions: [
      { kind: "tab", view: "motor", label: "うんどうタブ（切り替え遊び）" },
      { kind: "source", sourceId: "undou-nou", page: 6, label: "『子どもの脳を育てる「運動遊び」』TOPIC01〜03" },
    ],
    evidence: "『子どもの脳を育てる「運動遊び」』が中核に置く実行機能の研究にもとづきます",
  },
];
